import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  Divider,
  Avatar,
  Tooltip,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/HomeRounded';
import PeopleIcon from '@mui/icons-material/PeopleAltRounded';
import PaymentsIcon from '@mui/icons-material/PaymentsRounded';
import BuildIcon from '@mui/icons-material/BuildRounded';
import HouseIcon from '@mui/icons-material/ApartmentRounded';
import EventNoteIcon from '@mui/icons-material/EventNoteRounded';
import DescriptionIcon from '@mui/icons-material/DescriptionRounded';
import VisibilityIcon from '@mui/icons-material/VisibilityRounded';
import ShieldIcon from '@mui/icons-material/ShieldRounded';
import LocalParkingIcon from '@mui/icons-material/LocalParkingRounded';
import LogoutIcon from '@mui/icons-material/LogoutRounded';
import ExpandLess from '@mui/icons-material/ExpandLessRounded';
import ExpandMore from '@mui/icons-material/ExpandMoreRounded';
import { palette } from '../theme-site2';

// All paths are stored UNPREFIXED here. The renderer prepends PREFIX,
// which since Phase 1 of the migration (Apr 28, 2026) is empty — site2
// IS the main site. Kept as a constant in case we need to re-prefix
// for any future variant.
const PREFIX = '';

// Menu definition by role. Mirrors NavBar.js but expressed as data so
// the rendering logic is one tight loop instead of 600 lines of JSX.
// When new pages get added to NavBar.js, mirror them here too.
const MENU_BY_ROLE = {
  admin: [
    {
      title: 'Licensees',
      icon: PeopleIcon,
      items: [
        { label: 'View Licensees', path: '/licensees' },
        { label: 'Temporary Stays', path: '/temporary-stays' },
        { label: 'Office Queue (Enquiries)', path: '/general-enquiries' },
        { label: 'Licensee Archive', path: '/licensee-archive' },
        { label: 'Add Licensee', path: '/add-licensee' },
        { label: 'Invite Licensee', path: '/invite-licensee' },
        { label: 'Approve New Licensees', path: '/new-licensee-approval' },
        { label: 'Move Person Between Houses', path: '/move-person-between-places' },
        { label: 'Blacklist', path: '/blacklist' },
        { label: 'Portal Account Manager', path: '/portal-account-manager' },
      ],
    },
    {
      title: 'Moving',
      icon: HouseIcon,
      items: [
        { label: 'Move Management', path: '/move-management', badge: 'NEW' },
        { label: 'Scheduled Moves', path: '/scheduled-moves-dashboard' },
        { label: 'Moving Out List', path: '/moving-out-list' },
        { label: 'Licensee Lockbox Codes', path: '/lockbox-codes' },
      ],
    },
    {
      title: 'Viewings',
      icon: VisibilityIcon,
      items: [{ label: 'Viewings Schedule', path: '/viewings-schedule' }],
    },
    {
      title: 'Rent & Financial',
      icon: PaymentsIcon,
      items: [
        { label: 'Rent Management', path: '/admin-rent-v2' },
        { label: 'Payment History', path: '/payment-history' },
        { label: 'Discount Manager', path: '/discount-manager' },
        { label: 'Generate Deposit Link', path: '/generate-deposit-link' },
        { label: 'Generate Rent Link', path: '/generate-rent-link' },
        { label: 'Generated Links Tracker', path: '/generated-links-tracker' },
      ],
    },
    {
      title: 'Maintenance',
      icon: BuildIcon,
      items: [
        { label: 'Maintenance Dashboard', path: '/dashboard' },
        // Aug 18, 2026: same bug class as the Apr 28 restore below — these
        // three pages shipped into the old NavBar only and were unreachable
        // from this (live) sidebar. Keep BOTH navbars in sync when adding pages.
        { label: 'My Jobs', path: '/my-jobs' },
        { label: 'Garden Work', path: '/garden-work' },
        { label: 'Maintenance Checklist', path: '/maintenance-checklist' },
        { label: 'Maintenance Lockbox Codes', path: '/maintenance-lockbox-codes' },
        { label: 'Fire Alarm Codes', path: '/alarm-codes' },
        { label: 'Security Alarm Codes', path: '/security-alarm-codes' },
        { label: 'Bin Accounts', path: '/bins-management' },
        { label: 'Internet Accounts', path: '/internet-management' },
        { label: 'Energy Accounts', path: '/energy-management' },
        { label: 'Boiler Information', path: '/boiler-management' },
        { label: 'Suppliers', path: '/suppliers' },
        { label: 'Shopping List', path: '/shopping-list' },
        { label: 'House Setup', path: '/house-setup' },
        { label: 'Safety Equipment', path: '/safety-equipment-tracker' },
        { label: 'Labeling Tracker', path: '/labeling-tracker' },
        { label: 'Mattress Inventory', path: '/mattress-inventory' },
      ],
    },
    {
      title: 'Documents',
      icon: DescriptionIcon,
      items: [
        { label: 'Generate Documents', path: '/generate-documents' },
        { label: 'Reference Letter', path: '/generate-reference-letter' },
        { label: 'Portal Improvements', path: '/portal-improvements' },
      ],
    },
  ],

  god: [
    // Parking — god-only vertical, deliberately first in the list
    {
      title: 'Parking',
      icon: LocalParkingIcon,
      items: [
        { label: 'Parking Management', path: '/parking-admin' },
        { label: 'Customer Portal (preview)', path: '/parking' },
      ],
    },
    {
      title: 'Licensees',
      icon: PeopleIcon,
      items: [
        { label: 'View Licensees', path: '/licensees' },
        { label: 'Temporary Stays', path: '/temporary-stays' },
        { label: 'Office Queue (Enquiries)', path: '/general-enquiries' },
        { label: 'Licensee Archive', path: '/licensee-archive' },
        { label: 'Add Licensee', path: '/add-licensee' },
        { label: 'Invite Licensee', path: '/invite-licensee' },
        { label: 'Approve New Licensees', path: '/new-licensee-approval' },
        { label: 'Move Person Between Houses', path: '/move-person-between-places' },
        { label: 'Blacklist', path: '/blacklist' },
        { label: 'Portal Account Manager', path: '/portal-account-manager' },
      ],
    },
    {
      title: 'Moving',
      icon: HouseIcon,
      items: [
        { label: 'Move Management', path: '/move-management', badge: 'NEW' },
        { label: 'Scheduled Moves', path: '/scheduled-moves-dashboard' },
        { label: 'Moving Out List', path: '/moving-out-list' },
        { label: 'Licensee Lockbox Codes', path: '/lockbox-codes' },
      ],
    },
    {
      title: 'Viewings',
      icon: VisibilityIcon,
      items: [{ label: 'Viewings Schedule', path: '/viewings-schedule' }],
    },
    {
      title: 'Rent & Financial',
      icon: PaymentsIcon,
      items: [
        { label: 'Rent Management', path: '/admin-rent-v2' },
        { label: 'Payment History', path: '/payment-history' },
        { label: 'Discount Manager', path: '/discount-manager' },
        { label: 'Generate Deposit Link', path: '/generate-deposit-link' },
        { label: 'Generate Rent Link', path: '/generate-rent-link' },
        { label: 'Generated Links Tracker', path: '/generated-links-tracker' },
        { label: 'Refund Payment', path: '/refund-payment' },
        { label: 'Refund Search', path: '/refund-payment-search' },
        { label: 'Bulk Rent Generation', path: '/bulk-rent-generation' },
        { label: 'External Payments', path: '/external-payments' },
      ],
    },
    {
      title: 'Maintenance',
      icon: BuildIcon,
      items: [
        { label: 'Maintenance Dashboard', path: '/dashboard' },
        // Aug 18, 2026: same bug class as the Apr 28 restore below — these
        // three pages shipped into the old NavBar only and were unreachable
        // from this (live) sidebar. Keep BOTH navbars in sync when adding pages.
        { label: 'My Jobs', path: '/my-jobs' },
        { label: 'Garden Work', path: '/garden-work' },
        { label: 'Maintenance Checklist', path: '/maintenance-checklist' },
        { label: 'Maintenance Lockbox Codes', path: '/maintenance-lockbox-codes' },
        { label: 'Fire Alarm Codes', path: '/alarm-codes' },
        { label: 'Security Alarm Codes', path: '/security-alarm-codes' },
        { label: 'Bin Accounts', path: '/bins-management' },
        { label: 'Internet Accounts', path: '/internet-management' },
        { label: 'Energy Accounts', path: '/energy-management' },
        { label: 'Boiler Information', path: '/boiler-management' },
        { label: 'Suppliers', path: '/suppliers' },
        { label: 'Shopping List', path: '/shopping-list' },
        { label: 'House Setup', path: '/house-setup' },
        { label: 'Safety Equipment', path: '/safety-equipment-tracker' },
        { label: 'Labeling Tracker', path: '/labeling-tracker' },
        { label: 'Mattress Inventory', path: '/mattress-inventory' },
      ],
    },
    // "God Tools" section removed 2026-07-08 (David: never used from the nav).
    // The pages still exist and remain god-routed — direct URL only
    // (/usermanager, /webhooks-logs, /truelayer-audit, /pleo-analytics,
    // /latest-profit-csv, /analytics).
    {
      title: 'Documents',
      icon: DescriptionIcon,
      items: [
        { label: 'Generate Documents', path: '/generate-documents' },
        { label: 'Reference Letter', path: '/generate-reference-letter' },
        { label: 'Portal Improvements', path: '/portal-improvements' },
      ],
    },
  ],

  maintenance: [
    {
      title: 'Maintenance',
      icon: BuildIcon,
      items: [
        { label: 'Dashboard', path: '/dashboard' },
        // Aug 18, 2026: My Jobs / Garden Work / Maintenance Checklist shipped
        // into the old NavBar only — same bug class as the Apr 28 restore
        // below. Keep BOTH navbars in sync when adding pages.
        { label: 'My Jobs', path: '/my-jobs' },
        { label: 'Garden Work', path: '/garden-work' },
        { label: 'Maintenance Checklist', path: '/maintenance-checklist' },
        // Sep 2026 (Leonardo): maintenance can now view/filter office tickets
        { label: 'Office Queue (Enquiries)', path: '/general-enquiries' },
        // Apr 28, 2026: restored 4 items missing from the initial site2
        // sidebar — Licensee Lockbox Codes, Security Alarm Codes, House
        // Setup, Portal Improvements. Maintenance staff lost access to
        // them in the sidebar even though the routes still allowed them.
        { label: 'Licensee Lockbox Codes', path: '/lockbox-codes' },
        { label: 'Maintenance Lockbox Codes', path: '/maintenance-lockbox-codes' },
        { label: 'Fire Alarm Codes', path: '/alarm-codes' },
        { label: 'Security Alarm Codes', path: '/security-alarm-codes' },
        { label: 'Bin Accounts', path: '/bins-management' },
        { label: 'Internet Accounts', path: '/internet-management' },
        { label: 'Energy Accounts', path: '/energy-management' },
        { label: 'Boiler Information', path: '/boiler-management' },
        { label: 'Suppliers', path: '/suppliers' },
        { label: 'Shopping List', path: '/shopping-list' },
        { label: 'House Setup', path: '/house-setup' },
        { label: 'Safety Equipment', path: '/safety-equipment-tracker' },
        { label: 'Labeling Tracker', path: '/labeling-tracker' },
        { label: 'Mattress Inventory', path: '/mattress-inventory' },
        { label: 'Portal Improvements', path: '/portal-improvements' },
      ],
    },
    // Nov 2026 (Leonardo, approved by David): the "viewing team spreadsheet" he
    // asked for is the licensee roster, i.e. View Licensees, read-only for this
    // role (isLicenseeListReadOnly). The Viewings Schedule below stays.
    {
      title: 'Licensees',
      icon: PeopleIcon,
      items: [{ label: 'View Licensees', path: '/licensees' }],
    },
    // Oct 2026 (Leonardo, approved by David): the viewings schedule.
    {
      title: 'Viewings',
      icon: VisibilityIcon,
      items: [{ label: 'Viewings Schedule', path: '/viewings-schedule' }],
    },
  ],

  // Apr 28, 2026: staff/viewings/user menus rewritten to fully mirror
  // the old top-NavBar items per role. Initial site2 versions had
  // dropped a number of items (Lockbox Codes, Blacklist, Move
  // Management, Property Tracking, Maintenance utilities for viewings,
  // Services for tenants). Restored here.
  staff: [
    {
      title: 'Licensees',
      icon: PeopleIcon,
      items: [
        { label: 'View Licensees', path: '/licensees' },
        { label: 'Temporary Stays', path: '/temporary-stays' },
        { label: 'Office Queue (Enquiries)', path: '/general-enquiries' },
        { label: 'Add Licensee', path: '/add-licensee' },
        { label: 'Blacklist', path: '/blacklist' },
      ],
    },
    {
      title: 'Moving',
      icon: HouseIcon,
      items: [
        { label: 'Move Management', path: '/move-management', badge: 'NEW' },
        { label: 'Scheduled Moves', path: '/scheduled-moves-dashboard' },
        { label: 'Moving Out List', path: '/moving-out-list' },
        { label: 'Licensee Lockbox Codes', path: '/lockbox-codes' },
      ],
    },
    {
      title: 'Property Tracking',
      icon: BuildIcon,
      items: [
        { label: 'Safety Equipment', path: '/safety-equipment-tracker' },
        { label: 'Labeling Tracker', path: '/labeling-tracker' },
        { label: 'Mattress Inventory', path: '/mattress-inventory' },
      ],
    },
    {
      title: 'Documents',
      icon: DescriptionIcon,
      items: [
        { label: 'Generate Documents', path: '/generate-documents' },
        { label: 'Reference Letter', path: '/generate-reference-letter' },
      ],
    },
    {
      title: 'Generated Links',
      icon: PaymentsIcon,
      items: [
        { label: 'Generate Deposit Link', path: '/generate-deposit-link' },
      ],
    },
    {
      title: 'Portal',
      icon: DescriptionIcon,
      items: [
        { label: 'Portal Improvements', path: '/portal-improvements' },
      ],
    },
  ],

  viewings: [
    {
      title: 'Viewings',
      icon: VisibilityIcon,
      items: [{ label: 'Viewings Schedule', path: '/viewings-schedule' }],
    },
    {
      title: 'Licensees',
      icon: PeopleIcon,
      items: [
        { label: 'View Licensees', path: '/licensees' },
        { label: 'Temporary Stays', path: '/temporary-stays' },
        { label: 'Office Queue (Enquiries)', path: '/general-enquiries' },
        { label: 'Licensee Archive', path: '/licensee-archive' },
        { label: 'Add Licensee', path: '/add-licensee' },
        { label: 'Move Person Between Houses', path: '/move-person-between-places' },
        { label: 'Invite Licensee', path: '/invite-licensee' },
        { label: 'Approve New Licensees', path: '/new-licensee-approval' },
        { label: 'Blacklist', path: '/blacklist' },
        { label: 'Portal Account Manager', path: '/portal-account-manager' },
      ],
    },
    {
      title: 'Moving',
      icon: HouseIcon,
      items: [
        { label: 'Move Management', path: '/move-management', badge: 'NEW' },
        { label: 'Scheduled Moves', path: '/scheduled-moves-dashboard' },
        { label: 'Moving Out List', path: '/moving-out-list' },
        { label: 'Licensee Lockbox Codes', path: '/lockbox-codes' },
      ],
    },
    {
      title: 'Maintenance',
      icon: BuildIcon,
      items: [
        { label: 'Maintenance Dashboard', path: '/dashboard' },
        // Aug 18, 2026: same bug class as the Apr 28 restore below — these
        // three pages shipped into the old NavBar only and were unreachable
        // from this (live) sidebar. Keep BOTH navbars in sync when adding pages.
        { label: 'My Jobs', path: '/my-jobs' },
        { label: 'Garden Work', path: '/garden-work' },
        { label: 'Maintenance Checklist', path: '/maintenance-checklist' },
        { label: 'Maintenance Lockbox Codes', path: '/maintenance-lockbox-codes' },
        { label: 'Fire Alarm Codes', path: '/alarm-codes' },
        { label: 'Security Alarm Codes', path: '/security-alarm-codes' },
        { label: 'Bin Accounts', path: '/bins-management' },
        { label: 'Internet Accounts', path: '/internet-management' },
        { label: 'Energy Accounts', path: '/energy-management' },
        { label: 'Boiler Information', path: '/boiler-management' },
        { label: 'Suppliers', path: '/suppliers' },
        { label: 'Shopping List', path: '/shopping-list' },
        { label: 'House Setup', path: '/house-setup' },
      ],
    },
    {
      title: 'Property Tracking',
      icon: BuildIcon,
      items: [
        { label: 'Safety Equipment', path: '/safety-equipment-tracker' },
        { label: 'Labeling Tracker', path: '/labeling-tracker' },
        { label: 'Mattress Inventory', path: '/mattress-inventory' },
      ],
    },
    {
      title: 'Generated Links',
      icon: PaymentsIcon,
      items: [
        { label: 'Generate Deposit Link', path: '/generate-deposit-link' },
        { label: 'Generate Rent Link', path: '/generate-rent-link' },
        { label: 'Generated Links Tracker', path: '/generated-links-tracker' },
      ],
    },
    {
      title: 'Documents',
      icon: DescriptionIcon,
      items: [
        { label: 'Generate Documents', path: '/generate-documents' },
        { label: 'Reference Letter', path: '/generate-reference-letter' },
        { label: 'Portal Improvements', path: '/portal-improvements' },
      ],
    },
  ],

  user: [
    {
      title: 'My Account',
      icon: PeopleIcon,
      items: [
        { label: 'My Tickets', path: '/my-tickets' },
        { label: 'Profile', path: '/profile' },
        { label: 'Services', path: '/licensee-services' },
        { label: 'Pay Rent', path: '/pay-rent' },
        { label: 'Get Documents', path: '/get-documents' },
        { label: 'Report Issue', path: '/report-issue' },
        { label: 'Give Notice', path: '/give-notice' },
        { label: 'House Rules', path: '/house-rules' },
        { label: 'Message Us', path: '/message-us' },
      ],
    },
  ],

  // The two ISOLATED roles (House Share Solutions staff who share our
  // blacklist): one menu item each, nothing else. 'blacklist' is view-only,
  // 'blacklist_editor' can add/edit. See docs/core/BLACKLIST_ONLY_ROLES.md.
  blacklist: [
    {
      title: 'Blacklist',
      icon: ShieldIcon,
      items: [{ label: 'Blacklist', path: '/blacklist' }],
    },
  ],

  blacklist_editor: [
    {
      title: 'Blacklist',
      icon: ShieldIcon,
      items: [{ label: 'Blacklist', path: '/blacklist' }],
    },
  ],

  parking: [
    {
      title: 'Parking',
      icon: LocalParkingIcon,
      items: [
        { label: 'My Parking', path: '/parking' },
        { label: 'Report an Issue', path: '/parking/report-issue' },
      ],
    },
  ],
};

const SIDEBAR_WIDTH = 240;

// One section in the sidebar — collapsible header + child links.
// onNavigate fires when any child link is clicked — used by the
// mobile drawer to close itself after the user picks a destination.
function SidebarSection({ section, currentPath, onNavigate }) {
  const Icon = section.icon || EventNoteIcon;

  // Default to expanded if any child is the current page.
  const hasActiveChild = section.items.some(
    (item) => currentPath === `${PREFIX}${item.path}`
  );
  const [open, setOpen] = useState(hasActiveChild);

  return (
    <>
      <ListItemButton
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          borderRadius: 1,
          mx: 1,
          mb: 0.25,
          '&:hover': { backgroundColor: palette.sidebarBgHover },
        }}
      >
        <ListItemIcon sx={{ minWidth: 36, color: palette.sidebarText }}>
          <Icon fontSize="small" />
        </ListItemIcon>
        <ListItemText
          primary={section.title}
          // Force color here — Typography ignores the parent button's color
          // and falls back to theme.text.primary, which is near-black on
          // our dark sidebar (i.e. invisible). Same trick on every ListItemText.
          primaryTypographyProps={{
            fontSize: '0.875rem',
            fontWeight: 600,
            color: palette.sidebarText,
          }}
        />
        {open ? (
          <ExpandLess sx={{ color: palette.sidebarText, fontSize: 20 }} />
        ) : (
          <ExpandMore sx={{ color: palette.sidebarText, fontSize: 20 }} />
        )}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding sx={{ mb: 0.5 }}>
          {section.items.map((item) => {
            const fullPath = `${PREFIX}${item.path}`;
            const isActive = currentPath === fullPath;
            return (
              <ListItemButton
                key={item.path}
                component={Link}
                to={fullPath}
                onClick={onNavigate}
                sx={{
                  pl: 5.5,
                  py: 0.75,
                  mx: 1,
                  borderRadius: 1,
                  mb: 0.125,
                  backgroundColor: isActive
                    ? palette.sidebarActiveBg
                    : 'transparent',
                  '&:hover': {
                    backgroundColor: isActive
                      ? palette.sidebarActiveBg
                      : palette.sidebarBgHover,
                  },
                }}
              >
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: '0.8125rem',
                    fontWeight: isActive ? 600 : 400,
                    color: isActive
                      ? palette.sidebarActive
                      : palette.sidebarTextMuted,
                  }}
                />
                {item.badge && (
                  <Box
                    component="span"
                    sx={{
                      fontSize: '0.625rem',
                      fontWeight: 700,
                      px: 0.75,
                      py: 0.125,
                      borderRadius: 0.75,
                      backgroundColor: '#10b981',
                      color: '#ffffff',
                      letterSpacing: '0.04em',
                    }}
                  >
                    {item.badge}
                  </Box>
                )}
              </ListItemButton>
            );
          })}
        </List>
      </Collapse>
    </>
  );
}

// User pill at the bottom of the sidebar — avatar + email + role.
function SidebarUser({ currentUser, userRole, onLogout }) {
  if (!currentUser) {
    return (
      <Box sx={{ p: 2, borderTop: `1px solid ${palette.sidebarBorder}` }}>
        <ListItemButton
          component={Link}
          to={`${PREFIX}/login`}
          sx={{
            borderRadius: 1,
            color: palette.sidebarText,
            '&:hover': { backgroundColor: palette.sidebarBgHover },
          }}
        >
          <ListItemText
            primary="Sign in"
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: 500,
              color: palette.sidebarText,
            }}
          />
        </ListItemButton>
      </Box>
    );
  }

  const initial = (currentUser.email || '?')[0].toUpperCase();
  const displayName = currentUser.email
    ? currentUser.email.split('@')[0]
    : 'User';

  return (
    <Box
      sx={{
        p: 1.5,
        borderTop: `1px solid ${palette.sidebarBorder}`,
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
      }}
    >
      <Avatar
        sx={{
          width: 32,
          height: 32,
          backgroundColor: palette.accent,
          color: '#ffffff',
          fontSize: '0.8125rem',
          fontWeight: 600,
        }}
      >
        {initial}
      </Avatar>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography
          sx={{
            color: palette.sidebarText,
            fontSize: '0.8125rem',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {displayName}
        </Typography>
        <Typography
          sx={{
            color: palette.sidebarTextMuted,
            fontSize: '0.6875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          {userRole || 'guest'}
        </Typography>
      </Box>
      <Tooltip title="Sign out">
        <ListItemButton
          onClick={onLogout}
          sx={{
            flexShrink: 0,
            width: 32,
            height: 32,
            justifyContent: 'center',
            borderRadius: 1,
            p: 0,
            color: palette.sidebarTextMuted,
            '&:hover': {
              backgroundColor: palette.sidebarBgHover,
              color: palette.sidebarText,
            },
          }}
        >
          <LogoutIcon fontSize="small" />
        </ListItemButton>
      </Tooltip>
    </Box>
  );
}

// Dark left rail. Always visible on md+ screens; on small screens it's
// hidden (Site2Layout renders a slide-out version).
// onNavigate fires after any link click — Site2Layout passes the
// drawer-toggle so the drawer auto-closes on mobile.
function Site2NavBar({ variant = 'permanent', onNavigate }) {
  const { currentUser, userRole, logout } = useAuth();
  const location = useLocation();
  const sections = MENU_BY_ROLE[userRole] || [];

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error('Failed to log out', err);
    }
  };

  return (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        backgroundColor: palette.sidebarBg,
        color: palette.sidebarText,
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        position: variant === 'permanent' ? 'sticky' : 'static',
        top: 0,
        borderRight: `1px solid ${palette.sidebarBorder}`,
      }}
    >
      {/* Brand */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderBottom: `1px solid ${palette.sidebarBorder}`,
        }}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            backgroundColor: palette.accent,
            color: '#ffffff',
            fontSize: '0.875rem',
            fontWeight: 700,
            borderRadius: 1.25,
          }}
          variant="rounded"
        >
          L
        </Avatar>
        <Typography
          sx={{
            color: palette.sidebarText,
            fontSize: '1rem',
            fontWeight: 700,
            letterSpacing: '-0.01em',
          }}
        >
          LOBI
        </Typography>
      </Box>

      {/* Home (always visible) */}
      <Box sx={{ pt: 1.5 }}>
        <ListItemButton
          component={Link}
          to={PREFIX || '/'}
          onClick={onNavigate}
          sx={{
            mx: 1,
            mb: 0.5,
            borderRadius: 1,
            color:
              location.pathname === PREFIX || location.pathname === `${PREFIX}/`
                ? palette.sidebarActive
                : palette.sidebarText,
            backgroundColor:
              location.pathname === PREFIX || location.pathname === `${PREFIX}/`
                ? palette.sidebarActiveBg
                : 'transparent',
            '&:hover': { backgroundColor: palette.sidebarBgHover },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36, color: palette.sidebarText }}>
            <HomeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Home"
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: 500,
              color: palette.sidebarText,
            }}
          />
        </ListItemButton>

        {sections.length > 0 && (
          <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
            <Typography
              sx={{
                color: palette.sidebarTextMuted,
                fontSize: '0.6875rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}
            >
              Menu
            </Typography>
          </Box>
        )}
      </Box>

      {/* Menu sections — scrollable middle */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        <List component="nav" sx={{ py: 0 }}>
          {sections.map((section) => (
            <SidebarSection
              key={section.title}
              section={section}
              currentPath={location.pathname}
              onNavigate={onNavigate}
            />
          ))}
        </List>
      </Box>

      {/* User pill — pinned bottom */}
      <Divider sx={{ borderColor: palette.sidebarBorder }} />
      <SidebarUser
        currentUser={currentUser}
        userRole={userRole}
        onLogout={handleLogout}
      />
    </Box>
  );
}

export { SIDEBAR_WIDTH };
export default Site2NavBar;
